import React, { useMemo } from 'react';

/**
 * Cosmic Integration Report visuals — Item #5 from Updates-5_13_2026.
 *
 * Components:
 *   1. CrossLensRadar       — 7-domain radar/spider chart across 4 lenses
 *   2. SystemPressureMap    — Pressure heat-map across environments
 *   3. StabilityStrainChart — BHP vs PEI per lens, side-by-side bars
 *   4. DomainInteractionWeb — Domain-to-domain interaction strength web
 */

// ── Shared helpers ──

const LENS_DEFS = [
  { key: 'STUDENT_SUCCESS', label: 'Student', icon: '🎓', color: '#6366F1' },
  { key: 'PERSONAL_LIFESTYLE', label: 'Personal', icon: '🧍', color: '#8B5CF6' },
  { key: 'PROFESSIONAL_LEADERSHIP', label: 'Professional', icon: '💼', color: '#2563EB' },
  { key: 'FAMILY_ECOSYSTEM', label: 'Family', icon: '👨‍👩‍👧', color: '#059669' },
];

const DOMAIN_LABELS = [
  'Cognitive Control',
  'Emotional Regulation',
  'Behavioral Execution',
  'Internal State',
  'Social Cognition',
  'Self-Monitoring',
  'Environmental Demands',
];

const DOMAIN_KEYS = [
  'COGNITIVE_CONTROL',
  'EMOTIONAL_REGULATION',
  'BEHAVIORAL_EXECUTION',
  'INTERNAL_STATE_FACTORS',
  'SOCIAL_COGNITION',
  'SELF_MONITORING_AWARENESS',
  'ENVIRONMENTAL_DEMANDS',
];

const polarToCartesian = (cx, cy, r, angleDeg) => {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

const radarPoints = (values, cx, cy, maxR, count) => {
  return values
    .map((v, i) => {
      const angle = (360 / count) * i;
      const r = maxR * Math.min(1, Math.max(0, v));
      const pt = polarToCartesian(cx, cy, r, angle);
      return `${pt.x},${pt.y}`;
    })
    .join(' ');
};


// ── 1. Cross-Lens Radar Graph ──

export const CrossLensRadar = ({ data }) => {
  const lensProfiles = data?.cross_domain?.lens_profiles || {};

  const domainMap = useMemo(() => {
    const domainProfiles = data?.domains || data?.domain_profiles || [];
    const m = {};
    domainProfiles.forEach((dp) => {
      m[dp.name] = dp.score;
    });
    return m;
  }, [data]);

  // Build per-domain scores (0–1 range)
  const domainScores = DOMAIN_KEYS.map((k) => domainMap[k] || 0.5);
  const count = DOMAIN_KEYS.length;
  const cx = 180, cy = 180, maxR = 130;

  // Grid rings
  const rings = [0.25, 0.5, 0.75, 1.0];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <h3 className="text-base font-bold text-gray-800 mb-1 flex items-center gap-2">
        <span>🕸️</span> Cross-Lens Radar Profile
      </h3>
      <p className="text-xs text-gray-400 mb-4">
        Your 7-domain executive functioning strength profile
      </p>

      <div className="flex justify-center">
        <svg width="360" height="360" viewBox="0 0 360 360" className="max-w-full">
          {/* Grid rings */}
          {rings.map((pct) => (
            <polygon
              key={pct}
              points={radarPoints(
                Array(count).fill(pct),
                cx, cy, maxR, count
              )}
              fill="none"
              stroke="#E5E7EB"
              strokeWidth={pct === 1 ? 1.5 : 0.75}
            />
          ))}

          {/* Axis lines + labels */}
          {DOMAIN_LABELS.map((label, i) => {
            const angle = (360 / count) * i;
            const outerPt = polarToCartesian(cx, cy, maxR + 18, angle);
            const linePt = polarToCartesian(cx, cy, maxR, angle);
            return (
              <g key={i}>
                <line
                  x1={cx} y1={cy} x2={linePt.x} y2={linePt.y}
                  stroke="#D1D5DB" strokeWidth={0.5}
                />
                <text
                  x={outerPt.x} y={outerPt.y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize="7"
                  fill="#6B7280"
                  fontWeight="600"
                >
                  {label}
                </text>
              </g>
            );
          })}

          {/* Fill polygon */}
          <polygon
            points={radarPoints(domainScores, cx, cy, maxR, count)}
            fill="#6366F133"
            stroke="#6366F1"
            strokeWidth={2}
          />

          {/* Score dots */}
          {domainScores.map((score, i) => {
            const angle = (360 / count) * i;
            const pt = polarToCartesian(cx, cy, maxR * score, angle);
            return (
              <circle
                key={i}
                cx={pt.x} cy={pt.y} r={4}
                fill="#6366F1"
                stroke="white"
                strokeWidth={2}
              />
            );
          })}
        </svg>
      </div>

      {/* Per-lens mini indicators */}
      {Object.keys(lensProfiles).length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          {LENS_DEFS.map((lens) => {
            const lp = lensProfiles[lens.key];
            if (!lp) return null;
            const balance = ((lp.bhp || 0.5) - (lp.pei || 0.5));
            const balColor = balance >= 0 ? '#22C55E' : '#EF4444';
            return (
              <div key={lens.key} className="text-center p-2 rounded-lg bg-gray-50 border border-gray-100">
                <span className="text-lg">{lens.icon}</span>
                <p className="text-xs font-semibold text-gray-700 mt-1">{lens.label}</p>
                <p className="text-sm font-bold" style={{ color: balColor }}>
                  {(balance * 100).toFixed(0)}
                </p>
                <p className="text-[10px] text-gray-400">Balance</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


// ── 2. System Pressure Map ──

export const SystemPressureMap = ({ data }) => {
  const construct = data?.construct_scores || {};
  const lensProfiles = data?.cross_domain?.lens_profiles || {};
  const overallPei = construct.PEI_score || 0.5;
  const overallBhp = construct.BHP_score || 0.5;

  const envData = LENS_DEFS.map((d) => {
    const lp = lensProfiles[d.key];
    return {
      ...d,
      pei: lp?.pei || overallPei,
      bhp: lp?.bhp || overallBhp,
    };
  });

  const getPressureColor = (pei) => {
    if (pei >= 0.7) return '#DC2626';
    if (pei >= 0.55) return '#EA580C';
    if (pei >= 0.4) return '#EAB308';
    return '#22C55E';
  };

  const getPressureLabel = (pei) => {
    if (pei >= 0.7) return 'Critical';
    if (pei >= 0.55) return 'High';
    if (pei >= 0.4) return 'Moderate';
    return 'Low';
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <h3 className="text-base font-bold text-gray-800 mb-1 flex items-center gap-2">
        <span>🌡️</span> System Pressure Map™
      </h3>
      <p className="text-xs text-gray-400 mb-4">
        Environmental pressure intensity across each life domain
      </p>

      <div className="grid grid-cols-2 gap-3">
        {envData.map((env) => {
          const peiPct = Math.round(env.pei * 100);
          const color = getPressureColor(env.pei);
          const label = getPressureLabel(env.pei);
          return (
            <div
              key={env.key}
              className="relative rounded-xl p-4 border-2 overflow-hidden"
              style={{ borderColor: color + '66' }}
            >
              {/* Heat background */}
              <div
                className="absolute inset-0 opacity-10"
                style={{ backgroundColor: color }}
              />
              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">{env.icon}</span>
                  <span className="font-bold text-sm text-gray-800">{env.label}</span>
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-2xl font-extrabold" style={{ color }}>{peiPct}%</p>
                    <p className="text-xs font-semibold" style={{ color }}>{label} Pressure</p>
                  </div>
                  {/* Mini pressure bar */}
                  <div className="w-16 h-20 flex flex-col justify-end">
                    <div
                      className="w-full rounded-t-md transition-all duration-700"
                      style={{
                        height: `${peiPct}%`,
                        backgroundColor: color,
                        opacity: 0.7,
                      }}
                    />
                    <div className="w-full h-1 bg-gray-200 rounded-b-md" />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Overall system bar */}
      <div className="mt-4 bg-gray-50 rounded-xl p-3">
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs font-semibold text-gray-600">Overall System Pressure</span>
          <span className="text-sm font-bold" style={{ color: getPressureColor(overallPei) }}>
            {Math.round(overallPei * 100)}% — {getPressureLabel(overallPei)}
          </span>
        </div>
        <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${Math.round(overallPei * 100)}%`,
              background: `linear-gradient(to right, #22C55E, #EAB308, #EA580C, #DC2626)`,
            }}
          />
        </div>
      </div>
    </div>
  );
};


// ── 3. Stability vs Strain Chart ──

export const StabilityStrainChart = ({ data }) => {
  const construct = data?.construct_scores || {};
  const lensProfiles = data?.cross_domain?.lens_profiles || {};
  const overallPei = construct.PEI_score || 0.5;
  const overallBhp = construct.BHP_score || 0.5;

  const envData = LENS_DEFS.map((d) => {
    const lp = lensProfiles[d.key];
    return {
      ...d,
      bhp: lp?.bhp || overallBhp,
      pei: lp?.pei || overallPei,
    };
  });

  const maxVal = Math.max(...envData.map((e) => Math.max(e.bhp, e.pei)), 0.01);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <h3 className="text-base font-bold text-gray-800 mb-1 flex items-center gap-2">
        <span>📊</span> Stability vs Strain Across Environments
      </h3>
      <p className="text-xs text-gray-400 mb-4">
        Internal capacity (BHP) versus external demand (PEI) per life domain
      </p>

      <div className="space-y-4">
        {envData.map((env) => {
          const bhpPct = Math.round((env.bhp / maxVal) * 100);
          const peiPct = Math.round((env.pei / maxVal) * 100);
          const balance = env.bhp - env.pei;
          const statusColor = balance >= 0.1 ? '#22C55E' : balance >= -0.05 ? '#EAB308' : '#EF4444';
          const statusLabel = balance >= 0.1 ? 'Stable' : balance >= -0.05 ? 'Transitional' : 'Strained';

          return (
            <div key={env.key} className="bg-gray-50 rounded-xl p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{env.icon}</span>
                  <span className="text-sm font-bold text-gray-800">{env.label}</span>
                </div>
                <span
                  className="text-xs font-bold px-2 py-0.5 rounded-full"
                  style={{ color: statusColor, backgroundColor: statusColor + '18' }}
                >
                  {statusLabel}
                </span>
              </div>
              {/* BHP bar */}
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs text-blue-600 font-semibold w-8">BHP</span>
                <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-blue-500 transition-all duration-700"
                    style={{ width: `${bhpPct}%` }}
                  />
                </div>
                <span className="text-xs font-bold text-blue-600 w-10 text-right">
                  {Math.round(env.bhp * 100)}%
                </span>
              </div>
              {/* PEI bar */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-red-500 font-semibold w-8">PEI</span>
                <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-red-500 transition-all duration-700"
                    style={{ width: `${peiPct}%` }}
                  />
                </div>
                <span className="text-xs font-bold text-red-500 w-10 text-right">
                  {Math.round(env.pei * 100)}%
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex justify-center gap-6 mt-4 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <span className="w-3 h-2 rounded bg-blue-500" /> BHP (Internal Capacity)
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-2 rounded bg-red-500" /> PEI (External Pressure)
        </span>
      </div>
    </div>
  );
};


// ── 4. Domain Interaction Web ──

const WEB_NODES = [
  { key: 'cognitive', label: 'Cognitive', icon: '🧠', angle: 0 },
  { key: 'emotional', label: 'Emotional', icon: '💓', angle: 51 },
  { key: 'behavioral', label: 'Behavioral', icon: '⚡', angle: 103 },
  { key: 'internal', label: 'Internal', icon: '🌡️', angle: 154 },
  { key: 'social', label: 'Social', icon: '🤝', angle: 206 },
  { key: 'monitoring', label: 'Self-Monitor', icon: '👁️', angle: 257 },
  { key: 'environmental', label: 'Environment', icon: '🌍', angle: 309 },
];

export const DomainInteractionWeb = ({ data }) => {
  const domainProfiles = data?.domains || data?.domain_profiles || [];

  const cx = 160, cy = 160, maxR = 110;

  // Map flows to renderable edges
  const nodeMap = useMemo(() => {
    const m = {};
    WEB_NODES.forEach((n) => { m[n.key] = n; });
    return m;
  }, []);

  const renderFlows = useMemo(() => {
    const flows = data?.cross_domain?.flows || [];
    if (flows.length > 0) {
      return flows
        .filter((f) => nodeMap[f.from] && nodeMap[f.to])
        .map((f) => ({
          from: nodeMap[f.from],
          to: nodeMap[f.to],
          strength: f.strength || 0.3,
        }));
    }
    // Fallback: connect adjacent domains
    return WEB_NODES.map((n, i) => ({
      from: n,
      to: WEB_NODES[(i + 1) % WEB_NODES.length],
      strength: 0.3,
    }));
  }, [data, nodeMap]);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <h3 className="text-base font-bold text-gray-800 mb-1 flex items-center gap-2">
        <span>🔗</span> Domain Interaction Web
      </h3>
      <p className="text-xs text-gray-400 mb-4">
        How executive functioning domains influence each other under load
      </p>

      <div className="flex justify-center">
        <svg width="320" height="320" viewBox="0 0 320 320" className="max-w-full">
          {/* Connection lines */}
          {renderFlows.map((f, i) => {
            const p1 = polarToCartesian(cx, cy, maxR, f.from.angle);
            const p2 = polarToCartesian(cx, cy, maxR, f.to.angle);
            const opacity = Math.min(1, 0.2 + f.strength * 0.8);
            const width = 1 + f.strength * 3;
            return (
              <line
                key={i}
                x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
                stroke="#6366F1"
                strokeWidth={width}
                opacity={opacity}
                strokeLinecap="round"
              />
            );
          })}

          {/* Nodes */}
          {WEB_NODES.map((node) => {
            const pos = polarToCartesian(cx, cy, maxR, node.angle);
            const dp = domainProfiles.find(
              (d) => d.name?.toLowerCase().includes(node.key) ||
                     (node.key === 'monitoring' && d.name?.includes('SELF_MONITORING'))
            );
            const score = dp?.score || 0.5;
            const color = score >= 0.6 ? '#22C55E' : score >= 0.4 ? '#EAB308' : '#EF4444';
            return (
              <g key={node.key}>
                <circle cx={pos.x} cy={pos.y} r={22} fill="white" stroke={color} strokeWidth={2.5} />
                <text x={pos.x} y={pos.y + 1} textAnchor="middle" dominantBaseline="middle" fontSize="14">
                  {node.icon}
                </text>
                <text x={pos.x} y={pos.y + 32} textAnchor="middle" fontSize="7" fill="#6B7280" fontWeight="600">
                  {node.label}
                </text>
                <text x={pos.x} y={pos.y + 41} textAnchor="middle" fontSize="8" fill={color} fontWeight="bold">
                  {Math.round(score * 100)}%
                </text>
              </g>
            );
          })}

          {/* Center */}
          <circle cx={cx} cy={cy} r={18} fill="#F3F4F6" stroke="#D1D5DB" strokeWidth={1} />
          <text x={cx} y={cy + 1} textAnchor="middle" dominantBaseline="middle" fontSize="12">
            🌌
          </text>
        </svg>
      </div>
    </div>
  );
};

const CosmicVisuals = {
  CrossLensRadar,
  SystemPressureMap,
  StabilityStrainChart,
  DomainInteractionWeb,
};

export default CosmicVisuals;
